const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
export const isPosthogConfigured = Boolean(posthogKey && posthogHost);

const STORAGE_KEY = "rf-posthog-pending";

let client = null;
let loading = null;
let hurry = null;
let markReady;
const pending = [];
const ready = new Promise((resolve) => (markReady = resolve));

const takeStored = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    sessionStorage.removeItem(STORAGE_KEY);
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const pageContext = () => ({
  $current_url: window.location.href,
  $host: window.location.host,
  $pathname: window.location.pathname,
  $referrer: document.referrer || "$direct",
  $referring_domain: document.referrer ? new URL(document.referrer).host : "$direct"
});

const replay = (instance, [method, args, queuedAt, context]) => {
  if (method === "capture") {
    const [event, properties, options] = args;
    instance.capture(event, { ...context, ...properties }, { ...options, timestamp: new Date(queuedAt) });
  } else {
    instance[method](...args);
  }
};

const analyticsAllowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;

const call = (method, args) => {
  if (!analyticsAllowed()) return;
  if (client) {
    client[method](...args);
  } else if (loading) {
    pending.push([method, args, Date.now(), pageContext()]);
  }
};

const posthog = {
  capture: (...args) => call("capture", args),
  captureLog: (...args) => call("captureLog", args),
  hasPending: () => pending.length > 0,
  settle(timeout = 1000) {
    if (!pending.length) return Promise.resolve();
    hurry?.();
    return Promise.race([ready, new Promise((resolve) => setTimeout(resolve, timeout))]);
  }
};

if (!posthogKey) {
  if (import.meta.env.DEV) {
    throw new Error(
      "VITE_POSTHOG_KEY variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_POSTHOG_KEY is configured"
    );
  }
} else if (!posthogHost) {
  if (import.meta.env.DEV) {
    throw new Error(
      "VITE_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_POSTHOG_HOST is configured"
    );
  }
} else {
  const whenIdle = () =>
    new Promise((resolve) => {
      hurry = resolve;
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(resolve, { timeout: 2000 });
      } else {
        setTimeout(resolve, 0);
      }
    });

  const loadPosthog = () => {
    loading ??= whenIdle()
      .then(() => import("posthog-js"))
      .then(({ default: instance }) => {
        instance.init(posthogKey, {
          api_host: posthogHost,
          logs: {
            serviceName: "rise-and-fall-storefront",
            environment: import.meta.env.MODE
          },
          capture_exceptions: false,
          capture_pageview: false,
          opt_out_capturing_by_default: true,
          opt_out_persistence_by_default: true
        });
        return instance;
      });
    return loading;
  };

  let pageviewCaptured = false;

  const syncConsent = async () => {
    if (!analyticsAllowed()) {
      if (client?.is_capturing()) client.opt_out_capturing();
      if (!client) pageviewCaptured = false;
      pending.length = 0;
      takeStored();
      return;
    }

    if (!pageviewCaptured) {
      pageviewCaptured = true;
      pending.push(["capture", ["$pageview"], Date.now(), pageContext()]);
    }

    const instance = await loadPosthog();
    if (!analyticsAllowed()) return;

    const customer = window.posthogCustomer;
    const customerId = customer?.id ? String(customer.id) : null;
    const previousCustomerId = instance.get_property("$user_id");

    if (previousCustomerId && String(previousCustomerId) !== customerId) {
      instance.reset();
    }

    if (!instance.is_capturing()) {
      instance.opt_in_capturing({ captureEventName: false });
    }

    if (customerId) {
      instance.identify(customerId, {
        ...(customer.email && { email: customer.email }),
        ...(customer.name && { name: customer.name })
      });
    }

    client = instance;
    [...takeStored(), ...pending.splice(0)].forEach((entry) => replay(instance, entry));
    markReady();
  };

  window.addEventListener("pagehide", () => {
    if (!pending.length || !analyticsAllowed()) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...takeStored(), ...pending.splice(0)]));
    } catch {
      return;
    }
  });

  document.addEventListener("visitorConsentCollected", syncConsent);

  if (window.Shopify?.customerPrivacy) {
    syncConsent();
  } else {
    window.Shopify?.loadFeatures?.([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (!error) syncConsent();
    });
  }
}

export default posthog;
